import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import dayjs from "dayjs";
import { LastVisitBucket } from "@acto/gql/graphql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { DateRangeCalendarModal } from "../DateRangeSelector/DateRangeCalendarModal";
import { usePopover } from "../primitives/usePopover";
import { CheckMark, FilterPillTrigger, SelectItem, SelectPopover } from "../primitives/selectParts";
import { ChevronDown } from "@acto/web-common/components";

const Popover = styled(SelectPopover)(() => ({
  minWidth: "260px",
  maxHeight: "unset",
  overflowY: "visible",
}));

const Divider = styled("div")(({ theme }) => ({
  height: "1px",
  background: theme.managementView.colors.neutral[8],
  margin: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[8]}`,
}));

const CustomRangeItem = styled(SelectItem)(({ theme }) => ({
  justifyContent: "flex-start",
  gap: theme.managementView.spacing[8],
}));

const ActiveRangeLabel = styled("span")(({ theme }) => ({
  color: theme.managementView.colors.neutral[56],
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[440],
}));

export interface LastVisitRangeValue {
  startDate: string | null;
  endDate: string | null;
}

interface LastVisitFilterProps {
  bucket: LastVisitBucket | null;
  range: LastVisitRangeValue;
  onBucketChange: (bucket: LastVisitBucket | null) => void;
  onRangeChange: (range: LastVisitRangeValue) => void;
  disabled?: boolean;
}

export const LastVisitFilter = ({ bucket, range, onBucketChange, onRangeChange, disabled }: LastVisitFilterProps) => {
  const t = useTranslations("customers.filters");
  const tDateRange = useTranslations("dateRange");
  const { locale } = useLocale();
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const committedRange = useMemo(
    () =>
      range.startDate != null && range.endDate != null ? { startDate: range.startDate, endDate: range.endDate } : null,
    [range.startDate, range.endDate],
  );
  const hasRange = committedRange != null;

  const bucketOptions = useMemo(
    () => [
      { key: null as LastVisitBucket | null, label: t("lastVisitAll") },
      { key: LastVisitBucket.VisitedLast_30Days, label: t("lastVisitVisited30Days") },
      { key: LastVisitBucket.NotVisited_90Days, label: t("lastVisitNotVisited90Days") },
      { key: LastVisitBucket.NeverVisited, label: t("lastVisitNeverVisited") },
    ],
    [t],
  );

  const formatDate = useCallback((iso: string) => dayjs(iso).locale(locale).format("DD.MM.YYYY"), [locale]);

  const triggerLabel = useMemo(() => {
    if (committedRange) {
      return `${formatDate(committedRange.startDate)} – ${formatDate(committedRange.endDate)}`;
    }
    if (bucket != null) {
      const found = bucketOptions.find((opt) => opt.key === bucket);
      if (found) return found.label;
    }
    return t("lastVisitLabel");
  }, [committedRange, bucket, bucketOptions, formatDate, t]);

  const handleBucketSelect = (key: LastVisitBucket | null) => {
    if (hasRange) onRangeChange({ startDate: null, endDate: null });
    onBucketChange(key);
    close();
  };

  const handleOpenCalendar = () => {
    close();
    setIsCalendarOpen(true);
  };

  const handleCalendarApply = (next: { startDate: string; endDate: string }) => {
    if (bucket != null) onBucketChange(null);
    onRangeChange({ startDate: next.startDate, endDate: next.endDate });
    setIsCalendarOpen(false);
  };

  const activeRangeLabel = committedRange
    ? `${formatDate(committedRange.startDate)} – ${formatDate(committedRange.endDate)}`
    : null;

  return (
    <>
      <FilterPillTrigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
        {triggerLabel}
        <ChevronDown />
      </FilterPillTrigger>
      {isOpen &&
        createPortal(
          <Popover ref={popoverRef} style={position}>
            {bucketOptions.map((opt) => {
              const selected = !hasRange && opt.key === bucket;
              return (
                <SelectItem key={String(opt.key)} onClick={() => handleBucketSelect(opt.key)}>
                  <span style={{ flex: 1 }}>{opt.label}</span>
                  {selected && <CheckMark />}
                </SelectItem>
              );
            })}
            <Divider />
            <CustomRangeItem onClick={handleOpenCalendar}>
              <span>{tDateRange("selectRange")}</span>
              {activeRangeLabel && <ActiveRangeLabel>{activeRangeLabel}</ActiveRangeLabel>}
              {hasRange && <CheckMark />}
            </CustomRangeItem>
          </Popover>,
          document.body,
        )}
      {isCalendarOpen && (
        <DateRangeCalendarModal
          initialDateRange={committedRange ?? undefined}
          onApply={handleCalendarApply}
          onCancel={() => setIsCalendarOpen(false)}
        />
      )}
    </>
  );
};
